import { toast } from "react-hot-toast";

/**
 * Opções padrão para consultas (GET) que não podem virar tráfego infinito quando o backend falha.
 *
 * ## O problema que isto resolve
 *
 * `GET /api/group-pay/configuracao-email` passou a devolver 500 e a tela de "Busca de NF por
 * e-mail" começou a repetir a chamada a cada ~2 s, indefinidamente, com um toast de erro por
 * tentativa. Não era polling: era um ciclo de montagem.
 *
 * 1. A tela renderiza um spinner enquanto `isLoading` é verdadeiro e, com isso, **não monta** os
 *    componentes filhos.
 * 2. A consulta falha. Com `retry: false`, o estado vira `error` e `isLoading` vira falso.
 * 3. A tela renderiza os filhos — e dois deles (`ConfigurarAcessoDireto`, `ModalLogAcesso`)
 *    também chamam `useGetConfiguracaoEmail`. Cada um monta um novo observer da mesma query.
 * 4. Uma query que falhou tem `dataUpdatedAt === 0`, ou seja, está sempre *stale*. Com o padrão
 *    `refetchOnMount: true`, o observer novo dispara refetch.
 * 5. No react-query v3, o reducer faz `status: !state.dataUpdatedAt ? 'loading' : state.status`.
 *    Sem dado em cache, o refetch **volta o status para `loading`** — o spinner reaparece, os
 *    filhos desmontam, e o ciclo recomeça no passo 1.
 *
 * O período do ciclo é o tempo de um round-trip HTTP; daí os ~2 s observados. Nenhum limite de
 * tentativas existia porque nenhuma *tentativa* estava acontecendo: eram montagens novas.
 *
 * ## O que estas opções garantem
 *
 * - `refetchOnMount` deixa de disparar quando a query está em erro — é o que quebra o ciclo,
 *   sem abrir mão do refetch normal por `staleTime` no caso de sucesso.
 * - Uma falha rende no máximo `TENTATIVAS_ADICIONAIS` novas tentativas, com backoff exponencial,
 *   e depois o circuito fica aberto: nada mais sai do navegador até o usuário pedir (`refetch`)
 *   ou o cache expirar.
 * - O toast é identificado, então o react-hot-toast substitui o anterior em vez de empilhar.
 */
export const TENTATIVAS_ADICIONAIS = 2;

const ESPERA_BASE_MS = 1000;
const ESPERA_MAXIMA_MS = 8000;

export const opcoesConsultaResiliente = (identificadorDoToast: string, mensagemDeErro: string) => ({
  retry: TENTATIVAS_ADICIONAIS,
  retryDelay: (tentativa: number) => Math.min(ESPERA_BASE_MS * 2 ** tentativa, ESPERA_MAXIMA_MS),

  // Circuito aberto: enquanto a query estiver em erro, montar um novo observer não dispara
  // requisição nenhuma. Em sucesso, o comportamento padrão (refetch se estiver stale) continua.
  refetchOnMount: (query: { state: { status: string } }) => query.state.status !== "error",
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,

  onError: () => {
    toast.error(mensagemDeErro, { id: identificadorDoToast });
  },
});
